
import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Globe, X } from "lucide-react";
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator
} from "@/components/ui/dropdown-menu";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

interface MobileMenuProps {
  currentPageName: string;
  borderClass: string;
  children?: React.ReactNode;
  isOpen: boolean;
  onToggle: () => void;
}

const MobileMenu = ({ currentPageName, borderClass, children, isOpen, onToggle }: MobileMenuProps) => {
  const location = useLocation();
  const currentPath = location.pathname;
  const { user } = useAuth();
  
  const handleLinkClick = () => {
    onToggle(); // Close menu when link is clicked
  };

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  return (
    <>
      {/* Trigger button */}
      {children}
      
      {/* Fullscreen overlay menu */}
      {isOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm">
          <div className="fixed inset-0 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
            <div className="flex h-full flex-col">
              {/* Header with close button */}
              <div className="flex items-center justify-between p-6 border-b border-border">
                <h2 className="text-lg font-semibold text-foreground">Navigation</h2>
                <button 
                  onClick={onToggle}
                  className="h-10 w-10 rounded-md border border-border flex items-center justify-center hover:bg-accent text-foreground"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              
              {/* Navigation items */}
              <div className="flex-1 overflow-y-auto p-6">
                <nav className="space-y-2">
                  {/* Home - New first item */}
                  <Link 
                    to="/" 
                    onClick={handleLinkClick}
                    className={`block w-full p-4 text-left rounded-md hover:bg-accent transition-colors text-foreground ${currentPath === "/" ? "bg-accent text-accent-foreground" : ""}`}
                  >
                    Home
                  </Link>
                  
                  <Link 
                    to="/parts" 
                    onClick={handleLinkClick}
                    className={`block w-full p-4 text-left rounded-md hover:bg-accent transition-colors text-foreground ${currentPath === "/parts" ? "bg-accent text-accent-foreground" : ""}`}
                  >
                    Parts
                  </Link>
                  
                  <Link 
                    to="/tutorials" 
                    onClick={handleLinkClick}
                    className={`block w-full p-4 text-left rounded-md hover:bg-accent transition-colors text-foreground ${currentPath === "/tutorials" ? "bg-accent text-accent-foreground" : ""}`}
                  >
                    Guides
                  </Link>
                  
                  <Link 
                    to="/tools" 
                    onClick={handleLinkClick}
                    className={`block w-full p-4 text-left rounded-md hover:bg-accent transition-colors text-foreground ${currentPath === "/tools" ? "bg-accent text-accent-foreground" : ""}`}
                  >
                    Tools
                  </Link>
                  
                  <Link 
                    to="/roadmap" 
                    onClick={handleLinkClick}
                    className={`block w-full p-4 text-left rounded-md hover:bg-accent transition-colors text-foreground ${currentPath === "/roadmap" ? "bg-accent text-accent-foreground" : ""}`}
                  >
                    Roadmap
                  </Link>
                  
                  <Link 
                    to="/donate" 
                    onClick={handleLinkClick}
                    className={`block w-full p-4 text-left rounded-md hover:bg-accent transition-colors text-foreground ${currentPath === "/donate" ? "bg-accent text-accent-foreground" : ""}`}
                  >
                    Donate
                  </Link>
                  
                  <Link 
                    to="/saved" 
                    onClick={handleLinkClick}
                    className={`block w-full p-4 text-left rounded-md hover:bg-accent transition-colors text-foreground ${currentPath === "/saved" ? "bg-accent text-accent-foreground" : ""}`}
                  >
                    Saved Items
                  </Link>
                </nav>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MobileMenu;
